import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import useCoreProduct from "../../services/useCoreProduct";
import useUpdateCoreProduct, {
  UpdateCoreProductPayload,
} from "../../services/useUpdateCoreProduct";
import CoreProductForm from "../../components/CoreProductForm";
import { useState } from "react";

/**
 * Página de edição do produto do catálogo (core.Product)
 */
const CoreProductEdit = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const productId = idParam ? Number(idParam) : 0;
  const {
    data: product,
    isLoading,
    isError,
    error,
  } = useCoreProduct(productId);
  const { mutate: updateProduct, isLoading: isSubmitting } =
    useUpdateCoreProduct();
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleSubmit = (values: UpdateCoreProductPayload) => {
    if (!productId) return;

    setErrorMessage("");

    updateProduct(
      { id: productId, data: values },
      {
        onSuccess: () => {
          // Redirecionar para a página de detalhes após sucesso
          navigate(`/app/core-products/${productId}`);
        },
        onError: (err: any) => {
          console.error("Erro ao atualizar core product", err);

          const data = err?.response?.data ?? err;
          if (data && typeof data === "object") {
            const errorMessages = Object.keys(data).map((key) => {
              const value = (data as any)[key];
              const messages = Array.isArray(value) ? value : [value];
              return `${key}: ${messages.join(", ")}`;
            });
            setErrorMessage(errorMessages.join("\n"));
          } else {
            setErrorMessage("Erro ao atualizar produto. Tente novamente.");
          }
        },
      },
    );
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isError || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error
            ? "Erro ao carregar produto. Tente novamente."
            : "Produto não encontrado."}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/app/core-products")}
          >
            Voltar
          </Button>
        </Box>
      </Container>
    );
  }

  const handleCloseErrorMessage = () => {
    setErrorMessage("");
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" component="h1">
          Editar produto (novo catálogo)
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={handleCancel}>
          Cancelar
        </Button>
      </Box>

      <Card>
        <CardContent>
          <CoreProductForm
            initialValues={product}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            errorMessage={errorMessage}
            onCloseErrorMessage={handleCloseErrorMessage}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default CoreProductEdit;
